import React from "react";
import { Box, Text, Tag, Tooltip, HStack } from "@chakra-ui/react";

/**
 * Renders a section with a header icon/label and a list of chips.
 * Used for displaying cities, materials, and thematics in service details.
 * @param {Object} props
 * @param {React.ReactElement} props.icon - Icon element for the section header
 * @param {string} props.title - Section title text
 * @param {string} props.tooltipText - Tooltip for the header icon
 * @param {string[]} props.items - Array of string items to render as chips
 * @param {string} props.emptyText - Text shown when no items exist
 * @param {Object} [props.chipStyle] - Custom style overrides for chips
 * @param {Object} [props.allChip] - Config for the "all" chip: { label, style }
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function ChipListSection({ icon, title, tooltipText, items, emptyText, chipStyle, allChip, isMobile }) {
    return (
        <Box {...sectionContainerProps(isMobile)}>
            <SectionHeader
                icon={icon}
                title={title}
                tooltipText={tooltipText}
                isMobile={isMobile}
            />
            <Box display="flex" flexWrap="wrap" gap={1.5}>
                {renderChipContent(items, emptyText, chipStyle, allChip, isMobile)}
            </Box>
        </Box>
    );
}

/**
 * Renders the header row with an icon badge and title.
 * @param {Object} props
 * @param {React.ReactElement} props.icon - Icon element
 * @param {string} props.title - Section title
 * @param {string} props.tooltipText - Tooltip text for the icon
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement}
 */
function SectionHeader({ icon, title, tooltipText, isMobile }) {
    return (
        <HStack spacing={2} alignItems="center" mb={2}>
            <Tooltip label={tooltipText} hasArrow placement="top">
                <Box {...iconBadgeProps}>
                    {icon}
                </Box>
            </Tooltip>
            <Text {...titleProps(isMobile)}>
                {title}
            </Text>
        </HStack>
    );
}

/**
 * Renders chip content: "all" chip, item chips, or empty text.
 * @param {string[]} items - Array of items
 * @param {string} emptyText - Fallback text when empty
 * @param {Object} [chipStyle] - Custom chip styles
 * @param {Object} [allChip] - Config for the "all" chip
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {React.ReactElement|React.ReactElement[]}
 */
function renderChipContent(items, emptyText, chipStyle, allChip, isMobile) {
    if (allChip) {
        return <Tag size="sm" {...allChip.style}>{allChip.label}</Tag>;
    }

    if (items?.length) {
        return items.map((item, index) => (
            <Tag key={index} size="sm" {...(chipStyle || defaultChipProps(isMobile))}>
                {item}
            </Tag>
        ));
    }

    return (
        <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.75rem">
            {emptyText}
        </Text>
    );
}

/**
 * Returns style props for the section container box.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function sectionContainerProps(isMobile) {
    return {
        padding: isMobile ? "10px 12px" : "12px 14px",
        marginBottom: "8px",
        background: "rgba(255, 255, 255, 0.05)",
        backdropFilter: "blur(10px)",
        borderRadius: "10px",
        border: "1px solid rgba(255, 255, 255, 0.08)",
        transition: "all 0.2s ease",
        _hover: {
            background: "rgba(255, 255, 255, 0.08)",
            border: "1px solid rgba(255, 255, 255, 0.12)"
        }
    };
}

/** Style props for the icon badge wrapper */
const iconBadgeProps = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    borderRadius: "6px",
    background: "rgba(255, 255, 255, 0.08)",
    backdropFilter: "blur(10px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    flexShrink: 0
};

/**
 * Returns style props for the section title text.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function titleProps(isMobile) {
    return {
        color: "rgba(255, 255, 255, 0.7)",
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.5px"
    };
}

/**
 * Returns default style props for item chips.
 * @param {boolean} isMobile - Whether the viewport is mobile-sized
 * @returns {Object} Chakra style props
 */
function defaultChipProps(isMobile) {
    return {
        background: "rgba(255, 255, 255, 0.08)",
        backdropFilter: "blur(10px)",
        color: "rgba(255, 255, 255, 0.9)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        fontSize: isMobile ? "0.7rem" : "0.75rem",
        fontWeight: 500,
        height: "24px",
        borderRadius: "8px"
    };
}

export default ChipListSection;
